import { useCallback, useEffect, useRef } from 'react';
import { prefetchStream } from '@/services/streamingApi';
import type { SourceName, StreamRef } from '@/types/streaming';

/**
 * Debounced hover-prefetch: only fires after the pointer dwells on a
 * target for HOVER_DELAY_MS, so scanning past a grid of posters doesn't
 * fan out into one request per card. Aborts on leave and on unmount.
 */
const HOVER_DELAY_MS = 150;

export interface HoverPrefetchHandlers {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
}

export function useHoverPrefetch(ref: StreamRef, source?: SourceName): HoverPrefetchHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const start = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const ac = new AbortController();
      abortRef.current?.abort();
      abortRef.current = ac;
      void prefetchStream(ref, source, ac.signal);
    }, HOVER_DELAY_MS);
  }, [ref, source]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return {
    onMouseEnter: start,
    onMouseLeave: stop,
    onFocus: start,
    onBlur: stop,
  };
}
